import { Component, WritableSignal, computed, inject, input, output, viewChild } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { map, of, pipe, switchMap } from 'rxjs';
import { Action, SimpleGridComponent } from '@app/shared/components/simple-grid/simple-grid.component';
import { DeleteEventDataModel } from '@app/shared/components/simple-grid/simple-grid.model';
import {
  RentalAgreementDriversUpsertDialogComponent,
  RentalAgreementDriversUpsertDialogComponentConfig
} from './rental-agreement-drivers-upsert-dialog/rental-agreement-drivers-upsert-dialog.component';
import { RaVersion } from '../rental-agreements.model';
import { RentalAgreementDriverDetailsModel } from './rental-agreement-drivers.model';

type RentalAgreementsDriversConfig =
  | {
      raMode: 'create';
      rentalAgreementUid: string;
      drivers: WritableSignal<RentalAgreementDriverDetailsModel[]>;
    }
  | {
      raMode: 'update';
      rentalAgreementUid: string;
      rentalAgreementVersion: RaVersion;
      drivers: WritableSignal<RentalAgreementDriverDetailsModel[]>;
    }
  | {
      raMode: 'readonly';
      rentalAgreementUid: string;
      rentalAgreementVersion: RaVersion;
      drivers: WritableSignal<RentalAgreementDriverDetailsModel[]>;
    };

@Component({
  selector: 'app-rental-agreement-drivers',
  standalone: true,
  imports: [SimpleGridComponent],
  templateUrl: './rental-agreement-drivers.component.html',
  host: { class: 'd-flex flex-fill min-height-3' }
})
export class RentalAgreementDriversComponent {
  private _modalService = inject(NgbModal);

  mainDriverExists = false;

  config = input.required<RentalAgreementsDriversConfig>();

  driversChanged = output<void>();

  private _grid = viewChild(SimpleGridComponent);
  private _items = computed(() => this.config().drivers());

  refresh() {
    this._grid()?.refresh();
  }

  private isReadonly = computed(() => this.config().raMode === 'readonly');

  baseRowActions = computed<Action[]>(() => (this.isReadonly() ? ['details'] : ['create', 'update', 'delete']));

  listRentalAgreementDriversOperator = pipe(
    map(() => {
      const items = this._items();
      this.mainDriverExists = items.some((x) => !x.isAdditionalDriver);
      return items;
    })
  );

  createRentalAgreementDriverOperator = pipe(
    switchMap(() => {
      const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
      const modalRef = this._modalService.open(RentalAgreementDriversUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({
        mode: 'create',
        rentalAgreementUid: this.config().rentalAgreementUid,
        onlyAdditionalDriver: this.mainDriverExists
      } as RentalAgreementDriversUpsertDialogComponentConfig);
      return modalRef.closed.pipe(
        map((value: RentalAgreementDriverDetailsModel) => {
          this.config().drivers.update((list) => [...list, value]);
        })
      );
    })
  );

  updateRentalAgreementDriverOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
      const modalRef = this._modalService.open(RentalAgreementDriversUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({
        mode: 'update',
        onlyAdditionalDriver: this.mainDriverExists,
        driver: existing
      } as RentalAgreementDriversUpsertDialogComponentConfig);
      return modalRef.closed.pipe(
        map((value: RentalAgreementDriverDetailsModel) => {
          this.config().drivers.update((list) => list.map((x) => (x.uid === uid ? value : x)));
        })
      );
    })
  );

  detailsRentalAgreementValueOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
      const modalRef = this._modalService.open(RentalAgreementDriversUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({
        mode: 'readonly',
        driver: existing
      } as RentalAgreementDriversUpsertDialogComponentConfig);
      return modalRef.closed;
    })
  );

  deleteRentalAgreementDriverOperator = pipe(
    switchMap((data: DeleteEventDataModel) => {
      this.config().drivers.update((list) => list.filter((x) => x.uid !== data.uid));
      return of(undefined);
    })
  );

  columnDefs: ColDef[] = [
    { field: 'firstName', filter: false, floatingFilter: false },
    { field: 'lastName', filter: false, floatingFilter: false },
    { field: 'mobile', filter: false, floatingFilter: false },
    { field: 'licenseNumber', filter: false, floatingFilter: false },
    { field: 'email', filter: false, floatingFilter: false },
    { field: 'isAdditionalDriver', filter: false, floatingFilter: false }
  ];

  rowActionSelector = () => [];
}
